<template>
  <Teleport to="body"
    ><Transition name="item-panel"
      ><div
        v-if="visible"
        class="item-panel-layer"
        :style="zIndex ? { zIndex } : null"
        @click.self="requestClose"
      >
        <aside
          class="item-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="item-panel-title"
        >
          <header class="panel-header">
            <div>
              <button
                v-if="backLabel"
                type="button"
                class="panel-back-link"
                @click="requestBack"
              >
                ← {{ backLabel }}
              </button>
              <p>
                {{
                  createMode
                    ? "Nouvel objet"
                    : editing
                      ? "Modification de la fiche"
                      : "Objet de collection"
                }}
              </p>
              <h2 id="item-panel-title">{{ itemName }}</h2>
              <span v-if="item._key">#{{ item._key }}</span>
            </div>
            <button type="button" aria-label="Fermer" @click="requestClose">
              ×
            </button>
          </header>
          <nav class="panel-tabs" aria-label="Sections de la fiche">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              type="button"
              :class="{ active: activeTab === tab.id }"
              @click="activeTab = tab.id"
            >
              {{ tab.label }}
            </button>
          </nav>
          <div class="panel-body">
            <div v-if="loading" class="panel-state">
              Chargement de la fiche…
            </div>
            <div v-else-if="error" class="panel-state error">{{ error }}</div>
            <template v-else-if="editing">
              <section v-if="activeTab === 'summary'" class="edit-section">
                <div class="type-picker">
                  <button
                    v-for="type in types"
                    :key="type.value"
                    type="button"
                    :class="{ active: draft.type === type.value }"
                    @click="draft.type = type.value"
                  >
                    <span>{{ type.icon }}</span
                    ><strong>{{ type.label }}</strong
                    ><small>{{ type.help }}</small>
                  </button>
                </div>
                <div class="form-grid">
                  <label class="wide"
                    >Désignation *<input
                      v-model.trim="draftName"
                      required /></label
                  ><label
                    >Marque<ListPicker
                      v-model="draft.brand"
                      list="brands"
                      placeholder="Sélectionner une marque"
                      create-placeholder="Nouvelle marque"
                      allow-create /></label
                  ><label
                    >Plateforme<ListPicker
                      v-model="draft.model"
                      list="models"
                      placeholder="Sélectionner une plateforme"
                      create-placeholder="Nouvelle plateforme"
                      allow-create /></label
                  ><label
                    >Pays<ListPicker
                      v-model="draft.country"
                      list="countries"
                      placeholder="Sélectionner un pays" /></label
                  ><label
                    >État<select v-model="draft.condition">
                      <option value="">Non renseigné</option>
                      <option>Neuf</option>
                      <option>Très bon état</option>
                      <option>Bon état</option>
                      <option>État moyen</option>
                      <option>Mauvais état</option>
                    </select></label
                  ><label
                    >Boîte<select v-model="draft.boxed">
                      <option value="">Non renseigné</option>
                      <option value="true">Avec boîte</option>
                      <option value="false">Sans boîte</option>
                    </select></label
                  ><label
                    >Complétude<select v-model="draft.full">
                      <option value="">Non renseigné</option>
                      <option value="true">Complet</option>
                      <option value="false">Incomplet</option>
                    </select></label
                  ><label
                    >Notice<select v-model="draft.manual">
                      <option value="">Non renseigné</option>
                      <option value="true">Avec notice</option>
                      <option value="false">Sans notice</option>
                    </select></label
                  ><label
                    >Fonctionnement<select v-model="draft.working">
                      <option
                        v-for="state in workingStates"
                        :key="state.value"
                        :value="state.value"
                      >
                        {{ state.label }}
                      </option>
                    </select></label
                  ><label v-if="draft.type === 'console'"
                    >Serial matching<select v-model="draft.serial_matching">
                      <option value="">Non renseigné</option>
                      <option value="true">Oui</option>
                      <option value="false">Non</option>
                    </select></label
                  ><label class="wide"
                    >Inclus avec l’objet<input
                      v-model="draftIncluded"
                      placeholder="ex. 1 manette, câble AV, alimentation (séparés par des virgules)"
                  /></label>
                </div>
              </section>
              <section v-else-if="activeTab === 'details'" class="edit-section">
                <label class="standalone"
                  >Notes<textarea
                    v-model.trim="draft.description"
                    rows="7"
                    placeholder="État précis, accessoires, numéro de série…"
                  ></textarea>
                </label>
              </section>
              <section v-else-if="activeTab === 'origin'" class="edit-section">
                <div v-if="draft.offer_id" class="origin-card">
                  <div>
                    <span>Acquisition d’origine</span>
                    <strong>{{
                      item.origin?.title || `#${draft.offer_id}`
                    }}</strong>
                    <small>
                      {{
                        [
                          item.origin?.reference &&
                            `Réf. ${item.origin.reference}`,
                          item.origin?.packageRef &&
                            `Colis ${item.origin.packageRef}`,
                          `#${draft.offer_id}`,
                        ]
                          .filter(Boolean)
                          .join(" · ")
                      }}
                    </small>
                  </div>
                  <button
                    type="button"
                    @click="$emit('open-offer', draft.offer_id)"
                  >
                    Voir l’achat
                  </button>
                </div>
                <div v-else class="panel-empty">
                  Cet objet a été ajouté manuellement.
                </div>
              </section>
              <section v-else-if="activeTab === 'photos'" class="edit-section">
                <h3>Photos de l’objet</h3>
                <div v-if="existingImages.length" class="photo-grid">
                  <article v-for="image in existingImages" :key="image">
                    <img :src="imageUrl(image)" alt="" />
                    <button
                      type="button"
                      aria-label="Supprimer cette photo"
                      @click="removeExistingImage(image)"
                    >
                      ×
                    </button>
                  </article>
                </div>
                <!-- Chosen, not sent yet: sent with "Enregistrer la fiche" -->
                <div v-if="pendingImages.length" class="photo-grid">
                  <article
                    v-for="(file, index) in pendingImages"
                    :key="previewUrl(file)"
                    class="photo-pending"
                  >
                    <img :src="previewUrl(file)" alt="" />
                    <span class="photo-pending__label">À envoyer</span>
                    <button
                      type="button"
                      aria-label="Retirer cette photo"
                      @click="removePendingImage(index)"
                    >
                      ×
                    </button>
                  </article>
                </div>
                <label class="photo-drop"
                  ><input
                    type="file"
                    accept="image/*"
                    multiple
                    @change="selectImages"
                  /><span>{{
                    pendingImages.length || existingImages.length
                      ? "Ajouter des photos"
                      : "Choisir des photos"
                  }}</span
                  ><small v-if="pendingImages.length"
                    >{{ pendingImages.length }} photo{{
                      pendingImages.length > 1 ? "s" : ""
                    }}
                    à envoyer en enregistrant la fiche</small
                  ><small v-else
                    >Appareil photo ou galerie — plusieurs photos
                    possibles</small
                  ></label
                >
              </section>
              <section v-else class="edit-section">
                <label class="sale-toggle"
                  ><input v-model="draft.sold" type="checkbox" /><span
                    ><strong>Objet vendu</strong
                    ><small
                      >Active les informations relatives à la vente.</small
                    ></span
                  ></label
                >
                <div v-if="draft.sold" class="form-grid sale-fields">
                  <label
                    >Prix de vente<input
                      v-model.number="draft.soldPrice"
                      type="number"
                      min="0"
                      step="0.01" /></label
                  ><label
                    >Date de vente<input
                      v-model="draft.soldDate"
                      type="date" /></label
                  ><label class="wide"
                    >Canal de vente<input
                      v-model.trim="draft.soldMethod"
                      placeholder="Ex. eBay, Vinted…" /></label
                  ><label class="wide"
                    >Notes de vente<textarea
                      v-model.trim="draft.soldDescription"
                      rows="4"
                    ></textarea>
                  </label>
                </div>
              </section>
            </template>
            <template v-else>
              <section v-if="activeTab === 'summary'" class="view-section">
                <div class="item-hero">
                  <span>{{ itemName.charAt(0).toUpperCase() }}</span>
                  <div>
                    <h3>{{ itemName }}</h3>
                    <p>
                      {{
                        [item.brand, item.model].filter(Boolean).join(" · ") ||
                        "Marque et modèle à compléter"
                      }}
                    </p>
                  </div>
                  <b :class="item.sold ? 'sold' : 'available'">{{
                    item.sold ? "Vendu" : "En collection"
                  }}</b>
                </div>
                <dl class="data-grid">
                  <div>
                    <dt>Type</dt>
                    <dd>{{ typeLabel(item.type) }}</dd>
                  </div>
                  <div>
                    <dt>État</dt>
                    <dd>{{ item.condition || "À renseigner" }}</dd>
                  </div>
                  <div>
                    <dt>Plateforme</dt>
                    <dd>{{ item.model || "À renseigner" }}</dd>
                  </div>
                  <div>
                    <dt>Pays</dt>
                    <dd>{{ item.country || "À renseigner" }}</dd>
                  </div>
                  <div>
                    <dt>Fonctionnement</dt>
                    <dd>{{ workingLabel(item.working) }}</dd>
                  </div>
                  <div v-if="item.offer_id">
                    <dt>Réf. interne</dt>
                    <dd>
                      <button
                        type="button"
                        class="origin-link"
                        title="Voir l’achat d’origine"
                        @click="$emit('open-offer', item.offer_id)"
                      >
                        {{ originReference || `#${item.offer_id}` }}
                      </button>
                    </dd>
                  </div>
                </dl>
              </section>
              <section v-else-if="activeTab === 'details'" class="view-section">
                <h3>Informations de la fiche</h3>
                <dl class="detail-list">
                  <div>
                    <dt>Marque</dt>
                    <dd>{{ item.brand || "Non renseignée" }}</dd>
                  </div>
                  <div>
                    <dt>Plateforme</dt>
                    <dd>{{ item.model || "Non renseigné" }}</dd>
                  </div>
                  <div>
                    <dt>Boîte</dt>
                    <dd>
                      {{ booleanLabel(item.boxed, "Avec boîte", "Sans boîte") }}
                    </dd>
                  </div>
                  <div>
                    <dt>Complétude</dt>
                    <dd>
                      {{ booleanLabel(item.full, "Complet", "Incomplet") }}
                    </dd>
                  </div>
                  <div>
                    <dt>Notice</dt>
                    <dd>
                      {{
                        booleanLabel(item.manual, "Avec notice", "Sans notice")
                      }}
                    </dd>
                  </div>
                  <div>
                    <dt>Fonctionnement</dt>
                    <dd>
                      {{ workingLabel(item.working)
                      }}<template v-if="item.tested_at">
                        (testé le {{ formatDate(item.tested_at) }})</template
                      >
                    </dd>
                  </div>
                  <div v-if="item.type === 'console'">
                    <dt>Serial matching</dt>
                    <dd>
                      {{ booleanLabel(item.serial_matching, "Oui", "Non") }}
                    </dd>
                  </div>
                  <div>
                    <dt>Inclus</dt>
                    <dd>
                      {{
                        item.included?.length
                          ? item.included.join(", ")
                          : "Rien de précisé"
                      }}
                    </dd>
                  </div>
                  <div>
                    <dt>Notes</dt>
                    <dd>{{ item.description || "Aucune note" }}</dd>
                  </div>
                </dl>
              </section>
              <section v-else-if="activeTab === 'origin'" class="view-section">
                <h3>Provenance</h3>
                <div v-if="item.offer_id" class="origin-card">
                  <div>
                    <span>Acquisition d’origine</span>
                    <strong>{{
                      item.origin?.title || `#${item.offer_id}`
                    }}</strong>
                    <small>
                      {{
                        [
                          item.origin?.reference &&
                            `Réf. ${item.origin.reference}`,
                          item.origin?.packageRef &&
                            `Colis ${item.origin.packageRef}`,
                          `#${item.offer_id}`,
                        ]
                          .filter(Boolean)
                          .join(" · ")
                      }}
                    </small>
                  </div>
                  <div class="origin-actions">
                    <button
                      type="button"
                      @click="$emit('open-offer', item.offer_id)"
                    >
                      Voir l’achat
                    </button>
                    <button
                      v-if="item.origin?.package_id"
                      type="button"
                      @click="$emit('open-package', item.origin.package_id)"
                    >
                      Colis {{ item.origin.packageRef }} →
                    </button>
                  </div>
                </div>
                <div v-else class="panel-empty">
                  Cet objet a été ajouté manuellement.
                </div>
              </section>
              <section v-else-if="activeTab === 'photos'" class="view-section">
                <h3>Photos</h3>
                <div v-if="imageCount" class="photo-grid">
                  <a
                    v-for="image in item.images"
                    :key="image"
                    :href="imageUrl(image)"
                    target="_blank"
                    rel="noopener"
                    class="photo-link"
                    title="Voir la photo en grand"
                    ><img :src="imageUrl(image)" alt="" loading="lazy"
                  /></a>
                </div>
                <div v-else class="panel-empty">
                  Aucune photo associée à cet objet.
                </div>
              </section>
              <section v-else class="view-section">
                <h3>Vente</h3>
                <dl class="detail-list">
                  <div>
                    <dt>Statut</dt>
                    <dd>{{ item.sold ? "Vendu" : "Non vendu" }}</dd>
                  </div>
                  <div>
                    <dt>Prix de vente</dt>
                    <dd>
                      {{
                        formatMoney(
                          item.soldPrice ?? item.price ?? item.salePrice,
                        )
                      }}
                    </dd>
                  </div>
                  <div>
                    <dt>Canal</dt>
                    <dd>{{ item.soldMethod || "Non renseigné" }}</dd>
                  </div>
                  <div v-if="item.sale?.sale_id">
                    <dt>Vente</dt>
                    <dd>
                      <button
                        type="button"
                        class="origin-link"
                        @click="$emit('open-sale', item.sale.sale_id)"
                      >
                        {{ item.sale.title || `Vente #${item.sale.sale_id}` }} →
                      </button>
                    </dd>
                  </div>
                </dl>
              </section>
            </template>
          </div>
          <p v-if="saveError" class="save-error" role="alert">
            {{ saveError }}
          </p>
          <footer class="panel-footer">
            <template v-if="editing"
              ><button
                type="button"
                class="secondary"
                :disabled="saving"
                @click="cancelEdit"
              >
                {{
                  createMode ? "Annuler" : "Annuler les modifications"
                }}</button
              ><button
                type="button"
                class="primary"
                :disabled="saving || !draftName"
                @click="saveItem"
              >
                {{
                  saving
                    ? "Enregistrement…"
                    : createMode
                      ? "Ajouter l’objet"
                      : "Enregistrer la fiche"
                }}
              </button></template
            ><template v-else
              ><button type="button" class="secondary" @click="requestClose">
                Fermer</button
              ><button
                type="button"
                class="primary"
                :disabled="loading || !!error"
                @click="startEdit"
              >
                Modifier la fiche
              </button></template
            >
          </footer>
        </aside>
      </div></Transition
    ></Teleport
  >
</template>
<script>
import { formatDate as formatDateText } from "../../clock";
import { shrinkPhotos } from "../../photos";
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import axios from "axios";
import config from "@/config/config.js";
import ListPicker from "@/components/common/ListPicker.vue";
const normalizeBoolean = (value) =>
  value === true || value === "true"
    ? "true"
    : value === false || value === "false"
      ? "false"
      : "";
export default {
  name: "ItemQuickPanel",
  components: { ListPicker },
  props: {
    visible: { type: Boolean, default: false },
    itemId: { type: String, default: "" },
    createMode: { type: Boolean, default: false },
    // Opened over another sheet (see detailStack.js)
    backLabel: { type: String, default: "" },
    zIndex: { type: Number, default: 0 },
  },
  emits: [
    "close",
    "back",
    "saved-item",
    "open-offer",
    "open-package",
    "open-sale",
  ],
  setup(props, { emit }) {
    const item = ref({}),
      draft = reactive({}),
      loading = ref(false),
      saving = ref(false),
      error = ref(""),
      saveError = ref(""),
      editing = ref(false),
      activeTab = ref("summary"),
      pendingImages = ref([]),
      existingImages = ref([]),
      removedImages = ref([]),
      tabs = [
        { id: "summary", label: "Résumé" },
        { id: "details", label: "Fiche" },
        { id: "origin", label: "Provenance" },
        { id: "photos", label: "Photos" },
        { id: "sale", label: "Vente" },
      ],
      types = [
        {
          value: "console",
          label: "Console",
          icon: "▣",
          help: "Machine ou système",
        },
        { value: "game", label: "Jeu", icon: "●", help: "Titre ou support" },
        {
          value: "device",
          label: "Périphérique",
          icon: "⌁",
          help: "Accessoire ou extension",
        },
      ];
    // Internal reference given to the lot (purchase), if any
    const originReference = computed(
      () => item.value.origin?.reference || item.value.origin?.packageRef || "",
    );
    const itemName = computed(() =>
        props.createMode
          ? draftName.value || "Nouvel objet"
          : item.value.name || item.value.title || "Objet sans nom",
      ),
      draftName = computed({
        get: () =>
          draft.type === "game"
            ? draft.name || draft.title || ""
            : draft.title || draft.name || "",
        set: (value) => {
          if (draft.type === "game") {
            draft.name = value;
            delete draft.title;
          } else {
            draft.title = value;
            delete draft.name;
          }
        },
      }),
      imageCount = computed(() =>
        Array.isArray(item.value.images)
          ? item.value.images.length
          : Number(item.value.images?.count) || 0,
      );
    const authHeaders = () => ({
      Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    });
    const load = async () => {
      if (!props.itemId) return;
      loading.value = true;
      error.value = "";
      try {
        const response = await axios.get(
          `${config.apiUrl}/items/${props.itemId}`,
          { headers: authHeaders() },
        );
        item.value = response.data?.result || response.data || {};
      } catch (requestError) {
        console.error("Impossible de charger l’objet", requestError);
        error.value = "La fiche de cet objet est momentanément indisponible.";
      } finally {
        loading.value = false;
      }
    };
    const typeLabel = (type) =>
        ({ game: "Jeu", console: "Console", device: "Périphérique" })[type] ||
        "Objet",
      booleanLabel = (value, yes, no) =>
        value === true || value === "true"
          ? yes
          : value === false || value === "false"
            ? no
            : "Non renseigné",
      formatMoney = (value) =>
        new Intl.NumberFormat("fr-FR", {
          style: "currency",
          currency: "EUR",
        }).format(Number(value) || 0);
    const workingStates = [
      { value: "untested", label: "Non testé" },
      { value: "working", label: "Fonctionne" },
      { value: "partial", label: "Fonctionne partiellement" },
      { value: "broken", label: "HS" },
    ];
    const workingLabel = (value) =>
      workingStates.find((state) => state.value === (value || "untested"))
        ?.label;
    const formatDate = (value) => formatDateText(value);
    // "Inclus" is a list in the API, edited as comma-separated text
    const draftIncluded = computed({
      get: () =>
        Array.isArray(draft.included) ? draft.included.join(", ") : "",
      set: (value) => {
        draft.included = String(value)
          .split(",")
          .map((entry) => entry.trim())
          .filter(Boolean);
      },
    });
    const startEdit = () => {
      Object.keys(draft).forEach((key) => delete draft[key]);
      Object.assign(draft, item.value, {
        boxed: normalizeBoolean(item.value.boxed),
        full: normalizeBoolean(item.value.full),
        manual: normalizeBoolean(item.value.manual),
        serial_matching: normalizeBoolean(item.value.serial_matching),
        working: item.value.working || "untested",
        included: Array.isArray(item.value.included)
          ? [...item.value.included]
          : [],
      });
      saveError.value = "";
      existingImages.value = Array.isArray(item.value.images)
        ? [...item.value.images]
        : [];
      removedImages.value = [];
      editing.value = true;
      activeTab.value = "summary";
    };
    const cancelEdit = () => {
      if (props.createMode) {
        emit("close");
        return;
      }
      editing.value = false;
      pendingImages.value = [];
      existingImages.value = [];
      removedImages.value = [];
      saveError.value = "";
    };
    // Previews of the photos chosen, not sent yet (released when no longer shown)
    const previews = new Map();
    const previewUrl = (file) => {
      if (!previews.has(file)) previews.set(file, URL.createObjectURL(file));
      return previews.get(file);
    };
    const releasePreviews = () => {
      const kept = new Set(pendingImages.value);
      for (const [file, url] of previews)
        if (!kept.has(file)) {
          URL.revokeObjectURL(url);
          previews.delete(file);
        }
    };
    watch(pendingImages, releasePreviews);
    const removePendingImage = (index) => {
      pendingImages.value = pendingImages.value.filter(
        (_, position) => position !== index,
      );
    };
    // Phone photos shrunk as soon as they are chosen (src/photos.js)
    const selectImages = async (event) => {
      const files = Array.from(event.target.files || []);
      event.target.value = "";
      pendingImages.value = [
        ...pendingImages.value,
        ...(await shrinkPhotos(files)),
      ];
    };
    const imageUrl = (image) =>
      `${config.cdnUrl}/images/${item.value._key || props.itemId}/${String(
        image,
      )
        .split("/")
        .pop()}`;
    const removeExistingImage = (image) => {
      existingImages.value = existingImages.value.filter(
        (entry) => entry !== image,
      );
      if (!removedImages.value.includes(image)) removedImages.value.push(image);
    };
    // One photo per request (the API's proxy refuses more than 1 MB); a photo
    // sent leaves the list, so that a retry only sends the others
    const uploadImages = async (key) => {
      while (pendingImages.value.length) {
        const formData = new FormData();
        formData.append("files", pendingImages.value[0]);
        await axios.post(`${config.apiUrl}/items/${key}/images`, formData, {
          headers: authHeaders(),
        });
        pendingImages.value = pendingImages.value.slice(1);
      }
    };
    const saveItem = async () => {
      saving.value = true;
      saveError.value = "";
      let itemSaved = false;
      try {
        const data = { ...draft };
        delete data.images;
        if (data.boxed !== "") data.boxed = data.boxed === "true";
        else data.boxed = null;
        if (data.full !== "") data.full = data.full === "true";
        else data.full = null;
        if (!data.sold) {
          data.soldPrice = null;
          data.soldDate = null;
          data.soldMethod = "";
          data.soldDescription = "";
        }
        const response = await axios.post(
          `${config.apiUrl}/items`,
          { items: [data] },
          { headers: { ...authHeaders(), "Content-Type": "application/json" } },
        );
        let saved = response.data?.result?.[0] || data;
        const savedKey = saved._key || props.itemId;
        // A new item now exists: saving again (after a failed photo upload)
        // updates it instead of creating a second one
        if (savedKey) draft._key = savedKey;
        itemSaved = true;
        if (removedImages.value.length) {
          await axios.delete(`${config.apiUrl}/items/${savedKey}/images`, {
            data: { images: removedImages.value },
            headers: { ...authHeaders(), "Content-Type": "application/json" },
          });
          removedImages.value = [];
        }
        const uploaded = pendingImages.value.length > 0;
        await uploadImages(savedKey);
        if (uploaded) {
          const refreshed = await axios.get(
            `${config.apiUrl}/items/${saved._key || props.itemId}`,
            { headers: authHeaders() },
          );
          saved = refreshed.data?.result || refreshed.data || saved;
        }
        // origin is computed by GET /items/<id> only
        item.value = { ...saved, origin: item.value.origin };
        editing.value = false;
        pendingImages.value = [];
        existingImages.value = [];
        removedImages.value = [];
        emit("saved-item", item.value);
      } catch (requestError) {
        console.error("Impossible d’enregistrer l’objet", requestError);
        saveError.value = itemSaved
          ? "La fiche est enregistrée, mais des photos n’ont pas pu être envoyées. Réessayez : seules les photos restantes seront renvoyées."
          : "La fiche n’a pas pu être enregistrée. Vos modifications restent affichées.";
      } finally {
        saving.value = false;
      }
    };
    const requestClose = () => {
      if (
        editing.value &&
        !window.confirm("Quitter sans enregistrer les modifications ?")
      )
        return;
      emit("close");
    };
    const requestBack = () => {
      if (
        editing.value &&
        !window.confirm("Quitter sans enregistrer les modifications ?")
      )
        return;
      emit("back");
    };
    const escape = (event) => {
      if (event.key === "Escape" && props.visible) requestClose();
    };
    watch(
      () => [props.visible, props.itemId, props.createMode],
      ([visible]) => {
        if (visible) {
          activeTab.value = "summary";
          pendingImages.value = [];
          error.value = "";
          saveError.value = "";
          if (props.createMode || !props.itemId) {
            item.value = {};
            Object.keys(draft).forEach((key) => delete draft[key]);
            Object.assign(draft, {
              type: "game",
              name: "",
              title: "",
              brand: "",
              model: "",
              country: "",
              condition: "",
              boxed: "",
              full: "",
              manual: "",
              serial_matching: "",
              working: "untested",
              included: [],
              description: "",
              sold: false,
            });
            existingImages.value = [];
            removedImages.value = [];
            loading.value = false;
            editing.value = true;
          } else {
            editing.value = false;
            load();
          }
        }
      },
      { immediate: true },
    );
    onMounted(() => document.addEventListener("keydown", escape));
    onUnmounted(() => {
      document.removeEventListener("keydown", escape);
      previews.forEach((url) => URL.revokeObjectURL(url));
    });
    return {
      previewUrl,
      removePendingImage,
      activeTab,
      booleanLabel,
      draftIncluded,
      formatDate,
      workingLabel,
      workingStates,
      cancelEdit,
      draft,
      draftName,
      editing,
      error,
      existingImages,
      formatMoney,
      imageUrl,
      imageCount,
      item,
      itemName,
      originReference,
      load,
      loading,
      pendingImages,
      removeExistingImage,
      requestBack,
      requestClose,
      saveError,
      saveItem,
      saving,
      selectImages,
      startEdit,
      tabs,
      typeLabel,
      types,
    };
  },
};
</script>
<style scoped>
/* Opened over another sheet: way back to it */
.panel-back-link {
  display: inline-block;
  margin: 0 0 10px;
  padding: 0;
  border: 0;
  background: none;
  color: #2e6098;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.origin-card > .origin-actions {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-end;
  gap: 8px;
}

.item-panel-layer {
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(15, 23, 36, 0.45);
  backdrop-filter: blur(2px);
}
.item-panel {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  width: min(680px, 100%);
  height: 100%;
  flex-direction: column;
  background: #fff;
  box-shadow: -18px 0 55px rgba(15, 23, 36, 0.18);
  color: #202b3c;
  font-family: "DM Sans", sans-serif;
  padding-top: var(--safe-top);
  padding-bottom: var(--safe-bottom);
}
.panel-header {
  display: flex;
  justify-content: space-between;
  padding: 24px 28px 20px;
  border-bottom: 1px solid #e3e8ef;
}
.panel-header p {
  margin: 0 0 7px;
  color: #168a64;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.panel-header h2 {
  max-width: 550px;
  margin: 0;
  font-family: "Manrope", "DM Sans", sans-serif;
  font-size: 21px;
  line-height: 1.35;
}
.panel-header span {
  display: block;
  margin-top: 6px;
  color: #84909f;
  font-size: 11px;
}
.panel-header > button {
  width: 35px;
  height: 35px;
  border: 1px solid #d8dfe7;
  border-radius: 8px;
  background: #fff;
  color: #657184;
  font-size: 20px;
}
.panel-tabs {
  display: flex;
  gap: 24px;
  padding: 0 28px;
  border-bottom: 1px solid #e3e8ef;
  overflow-x: auto;
}
.panel-tabs button {
  position: relative;
  height: 52px;
  border: 0;
  background: none;
  color: #7a8595;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}
.panel-tabs button.active {
  color: #202b3c;
}
.panel-tabs button.active:after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
  background: #168a64;
  content: "";
}
.panel-body {
  min-height: 0;
  flex: 1;
  padding: 24px 28px;
  overflow: auto;
  background: #fafbfc;
}
.panel-state,
.panel-empty {
  display: grid;
  min-height: 180px;
  place-items: center;
  padding: 25px;
  border: 1px dashed #d8e0e8;
  border-radius: 10px;
  background: #fff;
  color: #7a8595;
  font-size: 12px;
  text-align: center;
}
.panel-state.error {
  color: #a64a43;
}
.item-hero {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 16px;
  border: 1px solid #e1e6ed;
  border-radius: 10px;
  background: #fff;
}
.item-hero > span {
  display: grid;
  width: 46px;
  height: 46px;
  place-items: center;
  border-radius: 8px;
  background: #eaf7f2;
  color: #168a64;
  font-weight: 800;
}
.item-hero > div {
  min-width: 0;
  flex: 1;
}
.item-hero h3 {
  overflow: hidden;
  margin: 0;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.item-hero p {
  margin: 5px 0 0;
  color: #7d8898;
  font-size: 11px;
}
.item-hero b {
  padding: 5px 8px;
  border-radius: 6px;
  font-size: var(--meta-size);
}
.item-hero b.available {
  background: #eaf7f2;
  color: #167457;
}
.item-hero b.sold {
  background: #edf4fb;
  color: #2e6098;
}
.data-grid,
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 11px;
  margin: 16px 0;
}
.data-grid div,
.detail-list div {
  padding: 13px;
  border: 1px solid #e5e9ee;
  border-radius: 8px;
  background: #fff;
}
.data-grid dt,
.detail-list dt {
  color: #84909f;
  font-size: var(--meta-size);
  text-transform: uppercase;
}
.data-grid dd,
.detail-list dd {
  margin: 5px 0 0;
  font-size: 12px;
  font-weight: 700;
}
.view-section > h3,
.edit-section > h3 {
  margin: 0 0 14px;
  font-size: 15px;
}
.detail-list {
  display: grid;
  gap: 9px;
  margin: 0;
}
.origin-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px;
  border: 1px solid #e1e6ed;
  border-radius: 9px;
  background: #fff;
}
.origin-card > div {
  display: grid;
  flex: 1;
  gap: 3px;
  min-width: 0;
}
.origin-card span,
.origin-card small {
  color: #7b8797;
  font-size: 11px;
}
.origin-card strong {
  overflow: hidden;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.origin-link {
  padding: 0;
  border: 0;
  background: none;
  color: #168a64;
  font: inherit;
  font-weight: 800;
  cursor: pointer;
}
.origin-card button {
  border: 0;
  background: none;
  color: #168a64;
  font-size: 11px;
  font-weight: 800;
}
.type-picker {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 9px;
}
.type-picker button {
  display: flex;
  min-height: 78px;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  padding: 12px;
  border: 1px solid #d8e0e8;
  border-radius: 9px;
  background: #fff;
  color: #526075;
  text-align: left;
}
.type-picker button.active {
  border-color: #75bba4;
  background: #edf8f4;
  color: #146b51;
}
.type-picker button span {
  font-size: 17px;
}
.type-picker button strong {
  margin-top: 4px;
  font-size: 12px;
}
.type-picker button small {
  margin-top: 3px;
  color: #7c8898;
  font-size: var(--meta-size);
}
.form-grid label,
.standalone {
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: #4d596a;
  font-size: 11px;
  font-weight: 700;
}
.form-grid .wide {
  grid-column: 1/-1;
}
.form-grid input,
.form-grid select,
.form-grid textarea,
.standalone textarea {
  width: 100%;
  min-height: 40px;
  padding: 8px 10px;
  border: 1px solid #d2dae4;
  border-radius: 7px;
  background: #fff;
  color: #283446;
  font: inherit;
  font-weight: 400;
}
.standalone textarea,
.form-grid textarea {
  resize: vertical;
}
.photo-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 9px;
  margin-bottom: 14px;
}
.photo-grid article {
  position: relative;
  overflow: hidden;
  aspect-ratio: 4/3;
  border: 1px solid #dce3eb;
  border-radius: 8px;
  background: #eef2f5;
}
.photo-grid img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.photo-grid a.photo-link {
  display: block;
  overflow: hidden;
  aspect-ratio: 4/3;
  border: 1px solid #dce3eb;
  border-radius: 8px;
  background: #eef2f5;
}
.photo-pending__label {
  position: absolute;
  left: 6px;
  bottom: 6px;
  padding: 2px 7px;
  border-radius: 10px;
  background: rgba(20, 30, 45, 0.78);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}
.photo-grid button {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 27px;
  height: 27px;
  border: 0;
  border-radius: 50%;
  background: rgba(20, 30, 45, 0.82);
  color: #fff;
}
.photo-drop {
  display: grid;
  min-height: 180px;
  place-items: center;
  padding: 25px;
  border: 1px dashed #b9c9d5;
  border-radius: 10px;
  background: #fff;
  text-align: center;
}
.photo-drop input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}
.photo-drop span {
  color: #168a64;
  font-size: 13px;
  font-weight: 800;
}
.photo-drop small {
  color: #7c8898;
  font-size: var(--meta-size);
}
.sale-toggle {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 15px;
  border: 1px solid #dce3eb;
  border-radius: 9px;
  background: #fff;
}
.sale-toggle input {
  width: 18px;
  height: 18px;
}
.sale-toggle span {
  display: flex;
  flex-direction: column;
}
.sale-toggle strong {
  font-size: 12px;
}
.sale-toggle small {
  margin-top: 3px;
  color: #7b8798;
  font-size: var(--meta-size);
}
.sale-fields {
  margin-top: 14px;
}
.save-error {
  margin: 0;
  padding: 10px 28px;
  border-top: 1px solid #f0d8d5;
  background: #fff5f4;
  color: #a4453f;
  font-size: 11px;
}
.panel-footer {
  display: flex;
  justify-content: flex-end;
  gap: 9px;
  padding: 16px 28px;
  border-top: 1px solid #e3e8ef;
  background: #fff;
}
.panel-footer button {
  height: 40px;
  padding: 0 15px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 800;
}
.secondary {
  border: 1px solid #d5dce5;
  background: #fff;
  color: #566276;
}
.primary {
  border: 0;
  background: #172235;
  color: #fff;
}
.primary:disabled,
.secondary:disabled {
  opacity: 0.5;
}
.item-panel-enter-active,
.item-panel-leave-active {
  transition: opacity 0.18s;
}
.item-panel-enter-from,
.item-panel-leave-to {
  opacity: 0;
}
@media (max-width: 600px) {
  .panel-header,
  .panel-body,
  .panel-footer {
    padding-inline: 18px;
  }
  .panel-tabs {
    padding-inline: 18px;
  }
  .data-grid,
  .form-grid {
    grid-template-columns: 1fr;
  }
  .form-grid .wide {
    grid-column: auto;
  }
  .type-picker {
    grid-template-columns: 1fr;
  }
  .type-picker button {
    min-height: 58px;
  }
  .panel-footer button {
    flex: 1;
  }
}
</style>
